import { useQuery } from '@apollo/client'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  RoutesProvider,
  ScrollRestoration,
} from '@faceup/router'
import { FuAdminRoleType, Institution } from '@faceup/utils'
import { wrapCreateBrowserRouterV7 } from '@sentry/react'
import { graphql } from '../__generated__'
import AdminLayout from '../AdminLayout/AdminLayout'
import PageSuspense from '../Components/PageSuspense'
import ViewerProvider from '../Contexts/AdminAccessRightsProvider'
import { routes, useRoutes } from '../hooks/useRoutes'
import RouteNotFound from '../RouteNotFound'
import Auth from '../utils/auth'
import AiHotlineSystemInstructions from './AiHotlineSystemInstructions'
import CreateAdmin from './CreateAdmin'
import GlobalTables from './GlobalTables'
import Hotlines from './Hotlines'
import InstitutionDetail from './InstitutionDetail'
import Institutions from './Institutions'
import { IntegrationHttpLogs } from './IntegrationHttpLogs'
import Login from './Login/Login'
import LoginRedirect from './Login/LoginRedirect'
import MaterialsView from './MaterialsView'
import PartnerDetail from './PartnerDetail'
import Partners from './Partners'
import ReportDetail from './ReportDetail'
import Reports from './Reports'
import SendEmails from './SendEmails'
import VonageHotlines from './VonageHotlines'

export const query = {
  AppRouterQuery: graphql(`
    query AppRouterQuery {
      fuAdminViewer {
        id
        role
        ...FuAdminLayout_admin
        ...FuViewerProvider_admin
      }
    }
  `),
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter)

const AppRouter = () => {
  const { client, data } = useQuery(query.AppRouterQuery, {
    onCompleted: async data => {
      // User has invalid JWT
      const viewer = data?.fuAdminViewer
      if (!viewer?.__typename) {
        logout()
      }
    },
    skip: !Auth.getJwt(),
  })

  const routeLinks = useRoutes()

  const viewer = data?.fuAdminViewer?.__typename === 'FuAdmin' ? data.fuAdminViewer : null
  const isAuthenticated = Boolean(viewer) || Boolean(Auth.getJwt())

  const logout = async () => {
    client.stop()
    await client.clearStore()
    Auth.logout()
    window.location.href = '/'
  }

  const unAuthenticatedRoutes: RouteObject[] = [
    {
      index: true,
      Component: Login,
    },
    {
      path: '/create-admin',
      Component: CreateAdmin,
    },
    {
      path: '*',
      Component: LoginRedirect,
    },
  ]

  const role = viewer?.role
  const isPsychologist = role === FuAdminRoleType.Psychologist
  const isManagerOrAdmin = role === FuAdminRoleType.Manager || role === FuAdminRoleType.Admin
  const isAdmin = role === FuAdminRoleType.Admin
  const isIntegrationDeveloper = role === FuAdminRoleType.IntegrationDeveloper
  // ENG-2130: the HTTP logs are visible to the admin and integration developer roles
  const canSeeIntegrationHttpLogs = isAdmin || isIntegrationDeveloper

  const authenticatedRoutes: RouteObject[] = [
    {
      path: routes.home,
      element: (
        <AdminLayout logout={logout} viewer={viewer}>
          <ViewerProvider viewer={viewer}>
            <PageSuspense>
              <Outlet />
            </PageSuspense>
          </ViewerProvider>
        </AdminLayout>
      ),
      children: [
        {
          path: '/',
          element: isPsychologist ? (
            <Navigate replace to={routes => routes.schools()} />
          ) : isIntegrationDeveloper ? (
            <Navigate replace to={routes => routes.integrationHttpLogs()} />
          ) : (
            <Navigate replace to={routes => routes.companies()} />
          ),
        },
        {
          path: routes.schools,
          element: <Institutions type={Institution.School} />,
        },
        {
          path: routes.companies,
          element: <Institutions type={Institution.Company} />,
        },
        {
          path: routes.institution,
          Component: InstitutionDetail,
        },
        ...(isManagerOrAdmin
          ? [
              {
                path: routes.partners,
                Component: Partners,
              },
              {
                path: routes.partner,
                Component: PartnerDetail,
              },
              {
                path: routes.materials,
                Component: MaterialsView,
              },
            ]
          : []),
        ...(isPsychologist
          ? [
              {
                path: routes.reports,
                Component: Reports,
              },
              {
                path: routes.report,
                Component: ReportDetail,
              },
            ]
          : []),
        ...(isManagerOrAdmin
          ? [
              {
                path: routes.hotlines,
                Component: Hotlines,
              },
              {
                path: routes.vonageHotlines,
                Component: VonageHotlines,
              },
              {
                path: routes.aiHotlineSystemInstructions,
                Component: AiHotlineSystemInstructions,
              },
            ]
          : []),
        ...(isAdmin
          ? [
              {
                path: routes.sendEmails,
                Component: SendEmails,
              },
              {
                path: routes.globalTables,
                Component: GlobalTables,
              },
            ]
          : []),
        ...(canSeeIntegrationHttpLogs
          ? [
              {
                path: routes.integrationHttpLogs,
                Component: IntegrationHttpLogs,
              },
            ]
          : []),
        {
          path: '*',
          Component: RouteNotFound,
        },
      ],
    },
  ]

  const router = sentryCreateBrowserRouter([
    {
      path: '/',
      element: (
        <RoutesProvider routes={routeLinks}>
          <ScrollRestoration />
          <Outlet />
        </RoutesProvider>
      ),
      children: isAuthenticated ? authenticatedRoutes : unAuthenticatedRoutes,
    },
  ])

  return <RouterProvider router={router} />
}

export default AppRouter
